// =============================================================================
// API Response Types (matching backend responses)
// =============================================================================

/** Generic API response wrapper */
export interface ApiResponse<T> {
  message: string;
  response: T;
}

/** Paginated response structure */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total?: number; // API returns 'total'
    totalItems?: number; // Some APIs may use 'totalItems' instead
    totalPages: number;
  };
}

// =============================================================================
// Product Types
// =============================================================================

/** Product/functionality that can have permissions assigned */
export interface Product {
  product_id: number;
  product_name: string;
  product_key: string;
  created_at?: string;
  updated_at?: string;
}

/** Permission level (Viewer, Editor, Manager) */
export interface PermissionLevel {
  level_id: number;
  level_name: string;
  weight: number;
}

// =============================================================================
// User Permission Types
// =============================================================================

/** Individual user permission for a product */
export interface UserPermission {
  productId: number;
  productName: string;
  productKey: string;
  levelId: number;
  levelName: string;
  weight: number;
}

/** User with their direct, group, and effective permissions */
export interface UserWithPermissions {
  id?: number;
  userId?: number;
  name?: string;
  email: string;
  displayName?: string | null;
  isAdmin?: boolean;
  isSuperadmin?: boolean;
  is_superadmin?: boolean; // API may return snake_case
  isTeam?: boolean;
  directPermissions: UserPermission[];
  groupPermissions: UserPermission[];
  effectivePermissions: UserPermission[];
  groups?: GroupMembership[];
}

/** Simplified group membership info */
export interface GroupMembership {
  groupId: number;
  groupName: string;
}

// =============================================================================
// Group Types
// =============================================================================

/** Group permission for a product */
export interface GroupPermission {
  productId: number;
  productName: string;
  productKey: string;
  levelId: number;
  levelName: string;
  weight: number;
}

/** Group member information */
export interface GroupMember {
  userId: number;
  name?: string;
  email: string;
  displayName?: string | null;
  joinedAt?: string;
}

/** User group with members and permissions */
export interface Group {
  groupId: number;
  groupName: string;
  description?: string | null;
  members: GroupMember[];
  permissions: GroupPermission[];
  createdAt?: string;
  updatedAt?: string;
}

// =============================================================================
// Legacy Types (for backward compatibility with existing UI)
// =============================================================================

/** Legacy user type for UI components */
export interface User {
  id: number;
  name: string;
  email: string;
  isSuperadmin: boolean;
  permissions: Record<string, string>;
  directPermissions?: Record<string, string>;
  groupPermissions?: Record<string, string>;
  effectivePermissions?: Record<string, string>;
  groups?: GroupMembership[];
}

/** Legacy user group type for UI components */
export interface UserGroup {
  id: number;
  name: string;
  description?: string;
  userIds: number[];
  members?: GroupMember[];
  permissions: Record<string, string>;
}

/** Permission removal context */
export interface PermissionToRemove {
  user: User;
  product: string;
  productId?: number;
}

// =============================================================================
// Transformation Helpers
// =============================================================================

/**
 * Convert backend UserWithPermissions to legacy User format
 */
export function transformToLegacyUser(userWithPermissions: UserWithPermissions): User {
  const permissions: Record<string, string> = {};
  const directPermissions: Record<string, string> = {};
  const groupPermissions: Record<string, string> = {};
  const effectivePermissions: Record<string, string> = {};

  userWithPermissions.directPermissions?.forEach(perm => {
    directPermissions[perm.productKey] = perm.levelName;
  });

  userWithPermissions.groupPermissions?.forEach(perm => {
    groupPermissions[perm.productKey] = perm.levelName;
  });

  userWithPermissions.effectivePermissions?.forEach(perm => {
    effectivePermissions[perm.productKey] = perm.levelName;
    permissions[perm.productKey] = perm.levelName;
  });

  return {
    id: userWithPermissions.id || userWithPermissions.userId || 0,
    name: userWithPermissions.name || userWithPermissions.displayName || userWithPermissions.email,
    email: userWithPermissions.email,
    isSuperadmin: userWithPermissions.isSuperadmin || false,
    permissions,
    directPermissions,
    groupPermissions,
    effectivePermissions,
    groups: userWithPermissions.groups,
  };
}

/**
 * Convert backend Group to legacy UserGroup format
 */
export function transformToLegacyGroup(group: Group): UserGroup {
  const permissions: Record<string, string> = {};
  
  group.permissions?.forEach(perm => {
    permissions[perm.productKey] = perm.levelName;
  });

  return {
    id: group.groupId,
    name: group.groupName,
    description: group.description || undefined,
    userIds: group.members?.map(m => m.userId) || [],
    members: group.members,
    permissions,
  };
}

/**
 * Convert legacy permissions Record to API format
 */
export function transformToApiPermissions(
  permissions: Record<string, string>,
  products: Product[],
  levels: PermissionLevel[]
): Array<{ productId: number; levelName: string }> {
  return Object.entries(permissions).map(([productKey, levelName]) => {
    const product = products.find(p => p.product_key === productKey);
    return {
      productId: product?.product_id || 0,
      levelName,
    };
  }).filter(p => p.productId > 0);
}
